/**
 * CreateMailboxWizard Component
 * Multi-step wizard for creating a new mailbox linked to a Keycloak user
 */
import React, { useState, useMemo, useCallback } from 'react';
import {
  XMarkIcon,
  UserIcon,
  GlobeAltIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  MagnifyingGlassIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { useKeycloakUsers, useDomains, useCreateMailbox } from '../../hooks/useMailboxes';
import type { KeycloakUser, Domain, CreateMailboxRequest } from '../../types/admin.types';

// Format bytes to human-readable
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// Quota presets in bytes
const QUOTA_PRESETS = [
  { label: '1 GB', value: 1 * 1024 * 1024 * 1024 },
  { label: '2 GB', value: 2 * 1024 * 1024 * 1024 },
  { label: '5 GB', value: 5 * 1024 * 1024 * 1024 },
  { label: '10 GB', value: 10 * 1024 * 1024 * 1024 },
  { label: '25 GB', value: 25 * 1024 * 1024 * 1024 },
  { label: '50 GB', value: 50 * 1024 * 1024 * 1024 },
];

type WizardStep = 'user' | 'domain' | 'details' | 'confirm';

interface CreateMailboxWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (mailbox: any) => void;
}

export function CreateMailboxWizard({
  isOpen,
  onClose,
  onSuccess,
}: CreateMailboxWizardProps) {
  // Step state
  const [currentStep, setCurrentStep] = useState<WizardStep>('user');

  // Form state
  const [selectedUser, setSelectedUser] = useState<KeycloakUser | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [localPart, setLocalPart] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [quotaBytes, setQuotaBytes] = useState(5 * 1024 * 1024 * 1024); // 5GB default
  const [customQuota, setCustomQuota] = useState('');

  // Search state
  const [userSearch, setUserSearch] = useState('');

  // Queries
  const { data: users, isLoading: usersLoading } = useKeycloakUsers({
    search: userSearch,
    withoutMailbox: false,
  });
  const { data: domains, isLoading: domainsLoading } = useDomains();

  // Mutation
  const createMutation = useCreateMailbox();

  // Filtered domains (only verified)
  const verifiedDomains = useMemo(() => {
    return domains?.filter((d: Domain) => d.status === 'verified' && d.is_active) || [];
  }, [domains]);

  // Generate email preview
  const emailPreview = useMemo(() => {
    if (!localPart || !selectedDomain) return '';
    return `${localPart}@${selectedDomain.domain}`;
  }, [localPart, selectedDomain]);

  // Step navigation
  const steps: { id: WizardStep; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { id: 'user', label: 'Select User', icon: UserIcon },
    { id: 'domain', label: 'Choose Domain', icon: GlobeAltIcon },
    { id: 'details', label: 'Mailbox Details', icon: EnvelopeIcon },
    { id: 'confirm', label: 'Confirm', icon: CheckCircleIcon },
  ];

  const currentStepIndex = steps.findIndex((s) => s.id === currentStep);

  const canProceed = useCallback(() => {
    switch (currentStep) {
      case 'user':
        return selectedUser !== null;
      case 'domain':
        return selectedDomain !== null;
      case 'details':
        return localPart.trim().length > 0 && quotaBytes > 0 && password.length >= 8 && password === confirmPassword;
      case 'confirm':
        return true;
      default:
        return false;
    }
  }, [currentStep, selectedUser, selectedDomain, localPart, quotaBytes, password, confirmPassword]);

  const handleNext = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex].id);
    }
  }, [currentStepIndex, steps]);

  const handleBack = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex].id);
    }
  }, [currentStepIndex, steps]);

  const handleUserSelect = useCallback((user: KeycloakUser) => {
    setSelectedUser(user);
    // Pre-fill local part and display name from user
    setLocalPart(user.username || user.email.split('@')[0]);
    setDisplayName(`${user.firstName} ${user.lastName}`.trim() || user.username);
  }, []);

  const handleDomainSelect = useCallback((domain: Domain) => {
    setSelectedDomain(domain);
  }, []);

  const handleQuotaPreset = useCallback((value: number) => {
    setQuotaBytes(value);
    setCustomQuota('');
  }, []);

  const handleCustomQuotaChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomQuota(value);
    const gbValue = parseFloat(value);
    if (!isNaN(gbValue) && gbValue > 0) {
      setQuotaBytes(gbValue * 1024 * 1024 * 1024);
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selectedUser || !selectedDomain) return;

    const request: CreateMailboxRequest = {
      keycloak_user_id: selectedUser.id,
      domain_id: selectedDomain.domain_id,
      local_part: localPart,
      display_name: displayName || undefined,
      quota_bytes: quotaBytes,
      password: password,
    };

    try {
      const mailbox = await createMutation.mutateAsync(request);
      onSuccess?.(mailbox);
      handleClose();
    } catch (error) {
      // Error handled by mutation
    }
  }, [selectedUser, selectedDomain, localPart, displayName, quotaBytes, password, createMutation, onSuccess]);

  const handleClose = useCallback(() => {
    // Reset state
    setCurrentStep('user');
    setSelectedUser(null);
    setSelectedDomain(null);
    setLocalPart('');
    setDisplayName('');
    setPassword('');
    setConfirmPassword('');
    setQuotaBytes(5 * 1024 * 1024 * 1024);
    setCustomQuota('');
    setUserSearch('');
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Create New Mailbox
            </h2>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = step.id === currentStep;
                const isCompleted = index < currentStepIndex;

                return (
                  <React.Fragment key={step.id}>
                    <div className="flex flex-col items-center">
                      <div
                        className={`flex items-center justify-center w-10 h-10 rounded-full ${
                          isActive
                            ? 'bg-lex-accent text-lex-accent-contrast'
                            : isCompleted
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {isCompleted ? (
                          <CheckCircleIcon className="h-5 w-5" />
                        ) : (
                          <Icon className="h-5 w-5" />
                        )}
                      </div>
                      <span
                        className={`mt-2 text-xs font-medium ${
                          isActive
                            ? 'text-lex-accent'
                            : isCompleted
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {step.label}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-2 ${
                          index < currentStepIndex
                            ? 'bg-green-500'
                            : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6 min-h-[300px]">
            {/* Step 1: Select User */}
            {currentStep === 'user' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Select a Keycloak user to link to this mailbox. Only users without an existing mailbox are shown.
                </p>

                {/* Search */}
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* User List */}
                <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  {usersLoading ? (
                    <div className="p-4 text-center text-gray-500">Loading users...</div>
                  ) : users && users.length > 0 ? (
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {users.map((user: KeycloakUser) => (
                        <button
                          key={user.id}
                          onClick={() => handleUserSelect(user)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                            selectedUser?.id === user.id
                              ? 'bg-lex-accent-soft border-l-4 border-lex-accent'
                              : ''
                          }`}
                        >
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 dark:text-white truncate">
                              {user.firstName} {user.lastName}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                              {user.email}
                            </p>
                          </div>
                          {selectedUser?.id === user.id && (
                            <CheckCircleIcon className="h-5 w-5 text-lex-accent" />
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center">
                      <UserIcon className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                      <p className="text-gray-500 dark:text-gray-400">
                        No users found without mailboxes
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Select Domain */}
            {currentStep === 'domain' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Choose the email domain for this mailbox.
                </p>

                {domainsLoading ? (
                  <div className="p-4 text-center text-gray-500">Loading domains...</div>
                ) : verifiedDomains.length > 0 ? (
                  <div className="grid gap-3">
                    {verifiedDomains.map((domain: Domain) => (
                      <button
                        key={domain.domain_id}
                        onClick={() => handleDomainSelect(domain)}
                        className={`flex items-center gap-4 p-4 border rounded-lg text-left transition-colors ${
                          selectedDomain?.domain_id === domain.domain_id
                            ? 'border-lex-accent bg-lex-accent-soft'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="flex-shrink-0 h-10 w-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                          <GlobeAltIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 dark:text-white">
                            @{domain.domain}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                              Verified
                            </span>
                            {domain.is_primary && (
                              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-lex-accent-soft text-lex-accent">
                                Primary
                              </span>
                            )}
                          </div>
                        </div>
                        {selectedDomain?.domain_id === domain.domain_id && (
                          <CheckCircleIcon className="h-6 w-6 text-lex-accent" />
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center border border-gray-200 dark:border-gray-700 rounded-lg">
                    <ExclamationCircleIcon className="h-12 w-12 mx-auto text-yellow-500 mb-2" />
                    <p className="text-gray-700 dark:text-gray-300 font-medium">
                      No verified domains available
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Please verify a domain before creating mailboxes.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Mailbox Details */}
            {currentStep === 'details' && (
              <div className="space-y-6">
                {/* Email Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={localPart}
                      onChange={(e) => setLocalPart(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ''))}
                      placeholder="username"
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <span className="text-gray-500 dark:text-gray-400">
                      @{selectedDomain?.domain}
                    </span>
                  </div>
                  {emailPreview && (
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Full address: <span className="font-medium text-gray-900 dark:text-white">{emailPreview}</span>
                    </p>
                  )}
                </div>

                {/* Display Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="John Smith"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 8 characters"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Used for IMAP/SMTP authentication in email clients like Outlook
                  </p>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                      confirmPassword && password !== confirmPassword
                        ? 'border-red-500 dark:border-red-500'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="mt-1 text-xs text-red-500">Passwords do not match</p>
                  )}
                </div>

                {/* Quota */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Storage Quota
                  </label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {QUOTA_PRESETS.map((preset) => (
                      <button
                        key={preset.value}
                        onClick={() => handleQuotaPreset(preset.value)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          quotaBytes === preset.value && !customQuota
                            ? 'bg-lex-accent text-lex-accent-contrast'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Or custom:</span>
                    <input
                      type="number"
                      value={customQuota}
                      onChange={handleCustomQuotaChange}
                      placeholder="Enter GB"
                      min="1"
                      className="w-24 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-400">GB</span>
                  </div>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Selected: <span className="font-medium">{formatBytes(quotaBytes)}</span>
                  </p>
                </div>
              </div>
            )}

            {/* Step 4: Confirm */}
            {currentStep === 'confirm' && (
              <div className="space-y-6">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Please review the mailbox details before creating.
                </p>

                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">User</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {selectedUser?.firstName} {selectedUser?.lastName}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Keycloak Email</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {selectedUser?.email}
                    </span>
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">New Email</span>
                      <span className="font-medium text-lex-accent">
                        {emailPreview}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Display Name</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {displayName || '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Storage Quota</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatBytes(quotaBytes)}
                    </span>
                  </div>
                </div>

                {createMutation.isError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                      <ExclamationCircleIcon className="h-5 w-5" />
                      <span className="font-medium">Error creating mailbox</span>
                    </div>
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {(createMutation.error as Error)?.message || 'An unexpected error occurred'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
            <button
              onClick={currentStepIndex > 0 ? handleBack : handleClose}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              {currentStepIndex > 0 ? (
                <>
                  <ChevronLeftIcon className="h-4 w-4" />
                  Back
                </>
              ) : (
                'Cancel'
              )}
            </button>

            {currentStep === 'confirm' ? (
              <button
                onClick={handleSubmit}
                disabled={createMutation.isPending}
                className="flex items-center gap-2 px-6 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createMutation.isPending ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Creating...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-4 w-4" />
                    Create Mailbox
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className="flex items-center gap-2 px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateMailboxWizard;
